import axios, { RawAxiosRequestHeaders } from 'axios'

import { getCerebroHeaders } from 'api/Cerebro/headers'
import { orgScopedApiClient } from 'api/orgScopedClient'
import { userHasCustomerServicePermissions } from 'helpers/featurePermissions'
import { selectDomainValue, selectUserFeaturePermissions } from 'selectors/auth'
import storeRegistry from 'utilities/storeRegistry'

interface AtlasTokenResponse {
    token: string
    expires_in: number
    atlas_uri: string
}

// Impersonation override used by the dashboard print page. The print lambda
// logs in as an automation user and renders a dashboard "as" its owner; for
// cerebro-backed widgets that owner identity rides on x-dsi-* headers. The
// Atlas/hulk stack has no equivalent because its identity is baked into the
// Atlas token, which is minted lazily inside this module's request interceptor
// — out of reach of the React context that holds the override. Threading the
// override down to the token mint (and onto the hulk/segment requests) lets
// Atlas scope the token to the owner instead of the automation user.
export interface AtlasUserOverride {
    userId?: string
    orgId?: string
    orgGroupId?: string
}

// Stash the override on the axios request config so the request interceptor can
// pick it up per request. Axios ignores unknown config fields on the wire, so
// this only travels as far as the interceptor.
declare module 'axios' {
    interface AxiosRequestConfig {
        userOverride?: AtlasUserOverride
    }
}

export interface OrgContext {
    orgId: string | null
    orgGroupId: string | null
    userId: string | null
}

let atlasToken: string | null = null
let atlasTokenExpiresAt = 0
let tokenOrgContext: OrgContext | null = null
let tokenPromise: Promise<string> | null = null
let tokenPromiseContext: OrgContext | null = null

function getCurrentOrgContext(override?: AtlasUserOverride): OrgContext {
    // An explicit override (print impersonation) wins over the ambient redux
    // identity — the automation user driving the print lambda has its own org
    // context that must not leak into the owner's token.
    if (
        override &&
        (override.userId || override.orgId || override.orgGroupId)
    ) {
        return {
            orgId: override.orgId ?? null,
            orgGroupId: override.orgGroupId ?? null,
            userId: override.userId ?? null,
        }
    }

    const state = storeRegistry.getState()
    if (!state) {
        return { orgId: null, orgGroupId: null, userId: null }
    }

    const isImpersonating = selectDomainValue(state, ['userIsImpersonating'])
    const isCustomerService = userHasCustomerServicePermissions(
        selectUserFeaturePermissions(state)
    )
    const organizationId = selectDomainValue(state, ['organizationId'])
    const organizationGroupId = selectDomainValue(state, [
        'organizationGroupId',
    ])

    if (organizationGroupId && isImpersonating && isCustomerService) {
        return { orgId: null, orgGroupId: organizationGroupId, userId: null }
    }
    return { orgId: organizationId ?? null, orgGroupId: null, userId: null }
}

export function sameOrgContext(a: OrgContext | null, b: OrgContext): boolean {
    return (
        a !== null &&
        a.orgId === b.orgId &&
        a.orgGroupId === b.orgGroupId &&
        a.userId === b.userId
    )
}

function clearAtlasToken(): void {
    atlasToken = null
    atlasTokenExpiresAt = 0
    tokenOrgContext = null
}

async function fetchAtlasToken(override?: AtlasUserOverride): Promise<string> {
    const orgContext = getCurrentOrgContext(override)
    // When impersonating, mint the token as the target user so Atlas scopes it
    // to the owner's org and segments. getCerebroHeaders maps the override to
    // the x-dsi-* impersonation headers cerebro honors on the mint request.
    const config =
        override && (override.userId || override.orgId || override.orgGroupId)
            ? {
                  headers: getCerebroHeaders({
                      userId: override.userId,
                      orgId: override.orgId,
                      orgGroupId: override.orgGroupId,
                  }),
              }
            : undefined
    const response = await orgScopedApiClient.get<AtlasTokenResponse>(
        '/api/atlas/token/',
        config
    )
    if (response.status >= 400) {
        throw new Error(`Atlas token fetch failed: ${response.status}`)
    }
    const { token, expires_in } = response.data
    atlasToken = token
    atlasTokenExpiresAt = Date.now() + expires_in * 1000
    tokenOrgContext = orgContext
    return token
}

async function getAtlasToken(override?: AtlasUserOverride): Promise<string> {
    const orgContext = getCurrentOrgContext(override)
    const cached =
        atlasToken &&
        sameOrgContext(tokenOrgContext, orgContext) &&
        Date.now() < atlasTokenExpiresAt - 60_000
    if (cached) {
        return atlasToken!
    }

    // Share an in-flight fetch only when it targets the same identity. A print
    // request impersonating an owner must not piggyback on (or be served by) the
    // automation user's token request, and vice versa.
    if (tokenPromise && sameOrgContext(tokenPromiseContext, orgContext)) {
        return tokenPromise
    }

    tokenPromiseContext = orgContext
    tokenPromise = fetchAtlasToken(override).finally(() => {
        tokenPromise = null
        tokenPromiseContext = null
    })
    return tokenPromise
}

export const atlasClient = axios.create({
    baseURL: ATLAS_API_ENDPOINT,
    headers: {
        'Content-Type': 'application/json',
    },
})

atlasClient.interceptors.request.use(async (config) => {
    const token = await getAtlasToken(config.userOverride)
    config.headers.Authorization = `Bearer ${token}`
    return config
})

atlasClient.interceptors.response.use(undefined, async (error) => {
    const original = error.config
    if (error.response?.status === 401 && !original.atlasRetried) {
        original.atlasRetried = true
        clearAtlasToken()
        const token = await getAtlasToken(original.userOverride)
        original.headers.Authorization = `Bearer ${token}`
        return atlasClient(original)
    }
    return Promise.reject(error)
})

export { clearAtlasToken }

export function getAtlasOrgHeaders(): RawAxiosRequestHeaders {
    const { orgId, orgGroupId } = getCurrentOrgContext()
    const headers: RawAxiosRequestHeaders = {}
    if (orgGroupId) {
        headers['x-dsi-org-group-id'] = orgGroupId
    } else if (orgId) {
        headers['x-dsi-org-id'] = orgId
    }
    return headers
}
